import { createAction } from '@reduxjs/toolkit';
import { v4 as uuidv4 } from 'uuid';

import { AppThunkDispatch } from '../store';
import {
  BaseTaskTemplate,
  NewWorkflowTemplate,
  RemoveTaskTemplatePayload,
  TaskTemplatePayload,
  WorkflowTemplate,
  WorkflowTemplateFormValues,
  WorkflowTemplateGroup,
} from 'data/models/WorkflowTemplates';
import { deleteWorkflowTemplate, fetchWorkflowTemplates, patchWorkflowTemplate, postWorkflowTemplate } from 'data/data-layer/workflowTemplates';

const SET_WORKFLOWS_TEMPLATE_LOADING = 'SET_WORKFLOWS_TEMPLATE_LOADING';
const LOAD_WORKFLOW_TEMPLATES = 'LOAD_WORKFLOW_TEMPLATES';
const CREATE_WORKFLOW_TEMPLATE = 'CREATE_WORKFLOW_TEMPLATE';
const UPDATE_WORKFLOW_TEMPLATE = 'UPDATE_WORKFLOW_TEMPLATE';
const DELETE_WORKFLOW_TEMPLATE = 'DELETE_WORKFLOW_TEMPLATE';

const SELECT_WORKFLOW_TEMPLATE = 'SELECT_WORKFLOW_TEMPLATE';
const RESET_SELECTED_WORKFLOW_TEMPLATE = 'RESET_SELECTED_WORKFLOW_TEMPLATE';

const CREATE_TASK_TEMPLATE = 'CREATE_TASK_TEMPLATE';
const UPDATE_TASK_TEMPLATE = 'UPDATE_TASK_TEMPLATE';
const DELETE_TASK_TEMPLATE = 'DELETE_TASK_TEMPLATE';

const CREATE_WORKFLOW_TEMPLATE_GROUP = 'CREATE_WORKFLOW_TEMPLATE_GROUP';
const UPDATE_WORKFLOW_TEMPLATE_GROUP = 'UPDATE_WORKFLOW_TEMPLATE_GROUP';
const DELETE_WORKFLOW_TEMPLATE_GROUP = 'DELETE_WORKFLOW_TEMPLATE_GROUP';
const RESET_WORKFLOW_TEMPLATE_GROUP_FORM = 'RESET_WORKFLOW_TEMPLATE_GROUP_FORM';

const SAVE_WORKFLOW_FORM_DRAFT = 'SAVE_WORKFLOW_FORM_DRAFT';
const RESET_WORKFLOW_FORM_DRAFT = 'RESET_WORKFLOW_FORM_DRAFT';

export const setWorkflowTemplatesLoading = createAction<boolean>(SET_WORKFLOWS_TEMPLATE_LOADING);
export const loadWorkflowTemplatesAction = createAction<WorkflowTemplate[]>(LOAD_WORKFLOW_TEMPLATES);
export const createWorkflowTemplateAction = createAction<WorkflowTemplate>(CREATE_WORKFLOW_TEMPLATE);
export const updateWorkflowTemplateAction = createAction<WorkflowTemplate>(UPDATE_WORKFLOW_TEMPLATE);
export const removeWorkflowTemplateAction = createAction<number>(DELETE_WORKFLOW_TEMPLATE);

export const setWorkflowTemplateAction = createAction<number>(SELECT_WORKFLOW_TEMPLATE);
export const resetSelectedWorkflowTemplateAction = createAction(RESET_SELECTED_WORKFLOW_TEMPLATE);

export const createTaskTemplateAction = createAction<TaskTemplatePayload>(CREATE_TASK_TEMPLATE);
export const updateTaskTemplateAction = createAction<TaskTemplatePayload>(UPDATE_TASK_TEMPLATE);
export const removeTaskTemplateAction = createAction<RemoveTaskTemplatePayload>(DELETE_TASK_TEMPLATE);

export const createWorkflowTemplateGroupAction = createAction(CREATE_WORKFLOW_TEMPLATE_GROUP);
export const updateWorkflowTemplateGroupAction = createAction<WorkflowTemplateGroup[]>(UPDATE_WORKFLOW_TEMPLATE_GROUP);
export const removeWorkflowTemplateGroupAction = createAction<number>(DELETE_WORKFLOW_TEMPLATE_GROUP);
export const resetWorkflowTemplateGroupFormAction = createAction(RESET_WORKFLOW_TEMPLATE_GROUP_FORM);

export const saveWorkflowFormDraftAction = createAction<WorkflowTemplateFormValues>(SAVE_WORKFLOW_FORM_DRAFT);
export const resetWorkflowFormDraftAction = createAction(RESET_WORKFLOW_FORM_DRAFT);

export const loadWorkflowTemplates =
  (partnerId: number, iaCode?: string | null) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(setWorkflowTemplatesLoading(true));
    const workflows: WorkflowTemplate[] = await fetchWorkflowTemplates(partnerId, dispatch, iaCode);
    dispatch(loadWorkflowTemplatesAction(workflows));
    dispatch(setWorkflowTemplatesLoading(false));
  };

export const createWorkflowTemplate =
  (workflow: NewWorkflowTemplate) =>
  async (dispatch: AppThunkDispatch): Promise<WorkflowTemplate | undefined> => {
    const createdWorkflow: WorkflowTemplate | undefined = await postWorkflowTemplate(workflow, dispatch);
    if (createdWorkflow) {
      dispatch(createWorkflowTemplateAction(createdWorkflow));
    }
    return createdWorkflow;
  };

export const updateWorkflowTemplate =
  (workflow: WorkflowTemplate) =>
  async (dispatch: AppThunkDispatch): Promise<WorkflowTemplate | undefined> => {
    const updatedWorkflow: WorkflowTemplate | undefined = await patchWorkflowTemplate(workflow, dispatch);
    if (updatedWorkflow) {
      dispatch(updateWorkflowTemplateAction(updatedWorkflow));
    }
    return updatedWorkflow;
  };

export const removeWorkflowTemplate =
  (workflowTemplateId: number) =>
  async (dispatch: AppThunkDispatch): Promise<number | undefined> => {
    const deletedWorkflowId: number | undefined = await deleteWorkflowTemplate(workflowTemplateId, dispatch);
    if (deletedWorkflowId) {
      dispatch(removeWorkflowTemplateAction(workflowTemplateId));
    }
    return deletedWorkflowId;
  };

export const setWorkflowTemplate = (workflowTemplateId: number) => async (dispatch: AppThunkDispatch) => {
  dispatch(setWorkflowTemplateAction(workflowTemplateId));
};

export const resetSelectedWorkflowTemplate = () => async (dispatch: AppThunkDispatch) => {
  dispatch(resetSelectedWorkflowTemplateAction());
};

export const createTaskTemplate =
  (taskTemplate: BaseTaskTemplate, groupIndex: number) =>
  async (dispatch: AppThunkDispatch): Promise<BaseTaskTemplate> => {
    const createdTemporaryTask: BaseTaskTemplate | undefined = {
      ...taskTemplate,
      temporaryUuid: uuidv4(),
    };
    const payload: TaskTemplatePayload = {
      groupIndex,
      taskTemplate: createdTemporaryTask,
    };
    dispatch(createTaskTemplateAction(payload));
    return createdTemporaryTask;
  };

export const updateTaskTemplate =
  (taskTemplate: BaseTaskTemplate, groupIndex: number) =>
  async (dispatch: AppThunkDispatch): Promise<BaseTaskTemplate> => {
    const payload: TaskTemplatePayload = {
      groupIndex,
      taskTemplate: taskTemplate,
    };
    dispatch(updateTaskTemplateAction(payload));
    return taskTemplate;
  };

export const removeTaskTemplate =
  (groupIndex: number, id?: number, temporaryUuid?: string) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    const payload: RemoveTaskTemplatePayload = {
      id,
      temporaryUuid,
      groupIndex,
    };
    dispatch(removeTaskTemplateAction(payload));
  };

export const createWorkflowTemplateGroup = () => async (dispatch: AppThunkDispatch) => {
  dispatch(createWorkflowTemplateGroupAction());
};

export const updateWorkflowTemplateGroup = (groups: WorkflowTemplateGroup[]) => async (dispatch: AppThunkDispatch) => {
  dispatch(updateWorkflowTemplateGroupAction(groups));
};

export const removeWorkflowTemplateGroup =
  (groupIndex: number) =>
  async (dispatch: AppThunkDispatch): Promise<void> => {
    dispatch(removeWorkflowTemplateGroupAction(groupIndex));
  };

export const resetWorkflowTemplateGroupForm = () => async (dispatch: AppThunkDispatch) => {
  dispatch(resetWorkflowTemplateGroupFormAction());
};
